import { isTheme, type Theme } from '@/contexts/theme';

/**
 * The theme painted on the page right now: the `data-theme` attribute ThemeProvider writes to
 * <html>, observed rather than read from React context.
 *
 * Chart colours come from computed styles, and those switch when the attribute does. The
 * provider writes it from a layout effect, which React runs after its children have rendered,
 * and during the theme crossfade a frame later still, so a chart that re-read the tokens when
 * the context value changed would read the previous theme's colours. Following the attribute
 * reads them once they have actually switched.
 */
export function getAppliedTheme(): Theme {
    if (typeof document === 'undefined') return 'light';
    const applied = document.documentElement.dataset.theme;
    return isTheme(applied) ? applied : 'light';
}

const listeners = new Set<() => void>();
let observer: MutationObserver | null = null;

function notify(): void {
    for (const listener of [...listeners]) listener();
}

/**
 * Calls `listener` after every change of the applied theme. One observer serves every
 * subscriber, and listeners run in subscription order, so the Chart.js defaults (subscribed
 * when the chart registry loads) are switched before any chart re-renders.
 */
export function subscribeAppliedTheme(listener: () => void): () => void {
    listeners.add(listener);
    if (!observer && typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
        observer = new MutationObserver(notify);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
        });
    }
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && observer) {
            observer.disconnect();
            observer = null;
        }
    };
}
